/**
 * ★ THE WHITE-LABEL DERIVATION ★
 *
 * Maps a tenant's config (ONE brand colour + a logo) onto the five `--brand*`
 * CSS custom properties that the design-system `.droom` layer consumes. This is
 * the single per-customer branding slot: the flow structure never changes, only
 * these five token values swap.
 *
 * ONE mechanism, reconciled onto the design system's `.droom[data-brand]` hook:
 *   • The package ships the `--brand*` slot + static `[data-brand="slug"]`
 *     blocks (the SSR / no-JS fallback for known tenants).
 *   • `deriveBrandTokens()` computes the five values from the tenant's single
 *     brand colour, and <Dataroom style={…}> writes them inline on the `.droom`
 *     root. Inline props win over the static block, so ANY tenant — a known slug
 *     or a config arriving live from the session API — reskins the whole flow,
 *     regardless of whether a static block exists.
 *
 * No component ever hardcodes a brand hex; the only brand hex in the system is
 * the tenant's own colour, which by definition arrives from config.
 */

// (Copied VERBATIM from vCIP_droom_4.0/src/theme/brand.ts; only the input type was localised —
//  svapp's TenantBranding has an OPTIONAL brandColor because §2 serves none today.)
export interface BrandInput {
  brandColor: string;
  brandColorStrong?: string;
  brandContrast?: string;
}
type TenantBranding = BrandInput;

/** The five CSS custom properties the flow reskins through. */
export interface BrandTokens {
  '--brand': string;
  '--brand-strong': string;
  '--brand-contrast': string;
  '--brand-subtle': string;
  '--brand-ring': string;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Parse #RGB or #RRGGBB into channels. Falls back to a neutral grey. */
function hexToRgb(hex: string): Rgb {
  const clean = hex.trim().replace(/^#/, '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  if (full.length !== 6 || /[^0-9a-fA-F]/.test(full)) {
    return { r: 120, g: 113, b: 108 }; // --text-muted-ish fallback
  }
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function toHex({ r, g, b }: Rgb): string {
  const h = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

function rgba({ r, g, b }: Rgb, alpha: number): string {
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Multiply channels toward black by `amount` (0..1) for a hover/pressed shade. */
function darken(rgb: Rgb, amount: number): Rgb {
  const f = 1 - amount;
  return { r: rgb.r * f, g: rgb.g * f, b: rgb.b * f };
}

/**
 * WCAG relative luminance (0 = black, 1 = white). Used to choose readable text
 * on the brand fill so a light brand doesn't get white-on-white text.
 */
function relativeLuminance({ r, g, b }: Rgb): number {
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Compute the full five-value brand token set from a tenant's branding. */
export function deriveBrandTokens(branding: TenantBranding): BrandTokens {
  const base = hexToRgb(branding.brandColor);
  const strong = branding.brandColorStrong
    ? branding.brandColorStrong
    : toHex(darken(base, 0.14));
  // On a light brand (luminance > ~0.55) use the near-black consumer ink;
  // otherwise white. Keeps CTA labels readable across the tenant spectrum.
  const contrast =
    branding.brandContrast ??
    (relativeLuminance(base) > 0.55 ? '#1C1712' : '#FFFFFF');
  return {
    '--brand': branding.brandColor,
    '--brand-strong': strong,
    '--brand-contrast': contrast,
    '--brand-subtle': rgba(base, 0.1),
    '--brand-ring': rgba(base, 0.3),
  };
}
